import { api } from "@shared/api/client";
import type {
  AdminClockingData,
  AdminPanelUser,
  AdminStatisticsData,
  UserSchedulePayload,
  VacationRequest,
} from "../types/admin-panel";

export type AdminPanelSummary = {
  users_count: number;
  vacation_requests_count: number;
  payrolls_count: number;
  contracts_count: number;
};

export type AdminPanelSectionPayload =
  | "summary"
  | "users"
  | "vacations"
  | "payrolls"
  | "contracts"
  | "clocking"
  | "statistics";

export type AdminPanelData = {
  summary?: AdminPanelSummary;
  users?: AdminPanelUser[];
  vacations?: VacationRequest[];
  payrolls?: PayrollRecord[];
  contracts?: ContractRecord[];
  vacation_rejection_reasons?: string[];
  clocking?: AdminClockingData;
  statistics?: AdminStatisticsData;
};

export type PayrollRecord = {
  id: number;
  empleado: string;
  mes: number;
  anio: number;
  tipo: string;
  archivo_nomina: string;
  fecha_subida: string;
};

export type ContractRecord = {
  id: number;
  empleado: string;
  nombre_archivo: string;
  fecha_subida: string;
};

const ADMIN_PANEL_TIMEOUT_MS = 90000;
export async function getAdminPanelData(params?: {
  sections?: AdminPanelSectionPayload[];
  clocking_date_from?: string;
  clocking_date_to?: string;
  clocking_rank_period?: "week" | "month" | "year";
  statistics_year?: number;
}): Promise<AdminPanelData> {
  const queryParams = {
    ...params,
    sections: params?.sections?.join(","),
  };

  const { data } = await api.get<AdminPanelData>("/admin-panel", {
    params: queryParams,
    timeout: ADMIN_PANEL_TIMEOUT_MS,
  });

  return data;
}

export async function reviewClockingJustification(
  id: number,
  estado_revision: "justificada" | "no_justificada",
): Promise<void> {
  await api.post(`/admin-panel/clocking/justifications/${id}/review`, {
    estado_revision,
  });
}

export type SaveUserPayload = {
  nombre: string;
  correo: string;
  rol: string;
  contrasena?: string;
  foto?: File | null;
  departamento?: string | null;
  empresa?: string | null;
  fecha_nacimiento?: string | null;
  fecha_inicio_contratacion?: string | null;
  vacation_partner_ids?: number[];
  schedules?: UserSchedulePayload[];
};

export type UpdateUserSchedulesPayload = {
  schedules: UserSchedulePayload[];
};

export type BulkUpdateUserSchedulesPayload = {
  user_ids: number[];
  schedules: UserSchedulePayload[];
};

function buildUserFormData(payload: SaveUserPayload) {
  const formData = new FormData();

  formData.append("nombre", payload.nombre);
  formData.append("correo", payload.correo);
  formData.append("rol", payload.rol);

  if (payload.contrasena) {
    formData.append("contrasena", payload.contrasena);
  }

  if (payload.foto) {
    formData.append("foto", payload.foto);
  }

  if (payload.departamento) {
    formData.append("departamento", payload.departamento);
  }

  if (payload.empresa) {
    formData.append("empresa", payload.empresa);
  }

  if (payload.fecha_nacimiento) {
    formData.append("fecha_nacimiento", payload.fecha_nacimiento);
  }

  if (payload.fecha_inicio_contratacion) {
    formData.append(
      "fecha_inicio_contratacion",
      payload.fecha_inicio_contratacion,
    );
  }

  Array.from(new Set(payload.vacation_partner_ids ?? [])).forEach(
    (partnerId) => {
      formData.append("vacation_partner_ids[]", String(partnerId));
    },
  );

  (payload.schedules ?? []).forEach((schedule, index) => {
    formData.append(
      `schedules[${index}][dia_semana]`,
      String(schedule.dia_semana),
    );
    formData.append(`schedules[${index}][hora_inicio]`, schedule.hora_inicio);
    formData.append(`schedules[${index}][hora_fin]`, schedule.hora_fin);
    formData.append(
      `schedules[${index}][tolerancia_minutos]`,
      String(schedule.tolerancia_minutos ?? 0),
    );
    formData.append(`schedules[${index}][activo]`, schedule.activo ? "1" : "0");
    formData.append(
      `schedules[${index}][vigente_desde]`,
      schedule.vigente_desde,
    );

    if (schedule.vigente_hasta) {
      formData.append(
        `schedules[${index}][vigente_hasta]`,
        schedule.vigente_hasta,
      );
    }
  });

  return formData;
}


export async function previewClockingJustification(
  id: number,
): Promise<{ blob: Blob; contentType: string }> {
  const res = await api.get(
    `/admin-panel/clocking/justifications/${id}/download`,
    {
      params: { preview: 1 },
      responseType: "blob",
    },
  );

  return {
    blob: res.data,
    contentType: String(res.headers["content-type"] ?? ""),
  };
}

export async function downloadClockingJustification(id: number): Promise<void> {
  const res = await api.get(
    `/admin-panel/clocking/justifications/${id}/download`,
    {
      responseType: "blob",
    },
  );

  openBlob(res.data, `justificacion-fichaje-${id}`);
}

export async function createAdminPanelUser(payload: SaveUserPayload) {
  const { data } = await api.post(
    "/admin-panel/users",
    buildUserFormData(payload),
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return data;
}

export async function updateAdminPanelUser(
  id: number,
  payload: SaveUserPayload,
) {
  const formData = buildUserFormData(payload);
  formData.append("_method", "PUT");

  const { data } = await api.post(`/admin-panel/users/${id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return data;
}

export async function updateUserSchedules(
  id: number,
  payload: UpdateUserSchedulesPayload,
): Promise<void> {
  await api.patch(`/admin-panel/users/${id}/schedules`, payload);
}

export async function bulkUpdateUserSchedules(
  payload: BulkUpdateUserSchedulesPayload,
): Promise<void> {
  await api.patch("/admin-panel/users/schedules/bulk", payload);
}

export async function deleteAdminPanelUser(id: number): Promise<void> {
  await api.delete(`/admin-panel/users/${id}`);
}

export async function approveVacation(id: number) {
  const { data } = await api.post<{ ok: boolean; vacation: VacationRequest }>(
    `/admin-panel/vacations/${id}/approve`,
  );
  return data;
}

export async function rejectVacation(
  id: number,
  payload: {
    motivo_rechazo: string;
    comentario_rechazo?: string;
  },
) {
  const { data } = await api.post<{ ok: boolean; vacation: VacationRequest }>(
    `/admin-panel/vacations/${id}/reject`,
    payload,
  );
  return data;
}

export type StorePayrollPayload = {
  usuario_id: number | "all";
  mes: number;
  anio: number;
  tipo: string;
  archivo: File;
};

export async function storePayroll(
  payload: StorePayrollPayload,
): Promise<void> {
  const fd = new FormData();

  fd.append("usuario_id", String(payload.usuario_id));
  fd.append("mes", String(payload.mes));
  fd.append("anio", String(payload.anio));
  fd.append("tipo", payload.tipo);
  fd.append("archivo", payload.archivo);

  await api.post("/admin-panel/payrolls", fd);
}

export async function storeContract(
  usuarioId: number | "all",
  archivos: File[],
): Promise<void> {
  const fd = new FormData();

  fd.append("usuario_id", String(usuarioId));

  archivos.forEach((file) => {
    fd.append("archivos[]", file);
  });

  await api.post("/admin-panel/contracts", fd);
}

export async function deleteContract(id: number): Promise<void> {
  await api.delete(`/admin-panel/contracts/${id}`);
}

export async function deletePayroll(id: number): Promise<void> {
  await api.delete(`/admin-panel/payrolls/${id}`);
}

export async function downloadPayroll(id: number): Promise<void> {
  const res = await api.get(`/admin-panel/payrolls/${id}/download`, {
    responseType: "blob",
  });

  openBlob(res.data, `nomina-${id}.pdf`);
}

export async function downloadContract(id: number): Promise<void> {
  const res = await api.get(`/admin-panel/contracts/${id}/download`, {
    responseType: "blob",
  });

  openBlob(res.data, `contrato-${id}`);
}

function openBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.target = "_blank";
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  a.remove();
  window.URL.revokeObjectURL(url);
}

export type CompanyRestDay = {
  id: number;
  fecha: string;
  nombre: string;
};

export async function getCompanyRestDays(year: number): Promise<CompanyRestDay[]> {
  const { data } = await api.get<{ data: CompanyRestDay[] }>("/admin-panel/rest-days", {
    params: { year },
  });
  return data.data;
}

export async function createCompanyRestDay(payload: {
  fecha: string;
  nombre: string;
}): Promise<CompanyRestDay> {
  const { data } = await api.post<{ data: CompanyRestDay }>("/admin-panel/rest-days", payload);
  return data.data;
}

export async function deleteCompanyRestDay(id: number): Promise<void> {
  await api.delete(`/admin-panel/rest-days/${id}`);
}
